import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, SpinnerGap, TreeStructure, MagnifyingGlass, Users, CaretDown, CaretRight } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = {
  superadmin: 'Super Admin', ceo: 'CEO', sales_manager: 'Sales Manager',
  recruiting_manager: 'Recruiting Manager', onsite_manager: 'Onsite Manager',
  sales_assistant_manager: 'Sales Asst. Manager', recruiting_assistant_manager: 'Recruiting Asst. Manager',
  sales_lead: 'Sales Lead', recruiting_lead: 'Recruiting Lead',
  sales_recruiter: 'Sales Recruiter', sales_technical_recruiter: 'Sales Recruiter & Technical Recruiter', technical_recruiter: 'Technical Recruiter',
};

const ROLE_COLORS = {
  superadmin: 'bg-slate-800 text-white', ceo: 'bg-blue-700 text-white',
  sales_manager: 'bg-indigo-100 text-indigo-700', recruiting_manager: 'bg-teal-100 text-teal-700',
  onsite_manager: 'bg-orange-100 text-orange-700',
  sales_assistant_manager: 'bg-indigo-50 text-indigo-600', recruiting_assistant_manager: 'bg-teal-50 text-teal-600',
  sales_lead: 'bg-blue-100 text-blue-700', recruiting_lead: 'bg-cyan-100 text-cyan-700',
  sales_recruiter: 'bg-amber-100 text-amber-700', sales_technical_recruiter: 'bg-amber-100 text-purple-700', technical_recruiter: 'bg-purple-100 text-purple-700',
};

const DEPTH_BORDER = ['border-l-orange-400', 'border-l-indigo-400', 'border-l-blue-400', 'border-l-teal-400', 'border-l-amber-400'];

const ROLE_PRIORITY = {
  onsite_manager: 0, sales_manager: 1, recruiting_manager: 2,
  sales_assistant_manager: 3, recruiting_assistant_manager: 4,
  sales_lead: 5, recruiting_lead: 6, sales_recruiter: 7, sales_technical_recruiter: 7, technical_recruiter: 8,
};

// Flatten tree into render list iteratively (avoids babel recursion)
function flattenTree(roots, childrenMap, expandedNodes, search) {
  const result = [];
  const stack = roots.map(r => ({ user: r, depth: 0 })).reverse();

  while (stack.length > 0) {
    const { user, depth } = stack.pop();
    const children = (childrenMap[user.id] || [])
      .sort((a, b) => (ROLE_PRIORITY[a.role] || 99) - (ROLE_PRIORITY[b.role] || 99));
    const hasChildren = children.length > 0;
    const isExpanded = expandedNodes.has(user.id);

    // Search filter
    if (search) {
      const nameMatch = user.name?.toLowerCase().includes(search) || user.email?.toLowerCase().includes(search);
      const hasDescendant = hasDescendantMatch(user.id, childrenMap, search);
      if (!nameMatch && !hasDescendant) continue;
    }

    result.push({ user, depth, hasChildren, isExpanded, childCount: children.length });

    if (hasChildren && isExpanded) {
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push({ user: children[i], depth: depth + 1 });
      }
    }
  }
  return result;
}

function hasDescendantMatch(uid, childrenMap, search) {
  const stack = [...(childrenMap[uid] || [])];
  while (stack.length) {
    const node = stack.pop();
    if (node.name?.toLowerCase().includes(search) || node.email?.toLowerCase().includes(search)) return true;
    const kids = childrenMap[node.id] || [];
    for (let i = 0; i < kids.length; i++) stack.push(kids[i]);
  }
  return false;
}

const ReportingStructurePage = () => {
  const navigate = useNavigate();
  const { isSuperadmin, isCeo } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(API_URL + '/api/reporting-structure', { withCredentials: true });
      const data = res.data || [];
      setUsers(data);
      // Auto-expand all nodes that have children
      const cMap = {};
      data.forEach(u => {
        (u.reporting_managers || []).forEach(mid => {
          if (!cMap[mid]) cMap[mid] = [];
          cMap[mid].push(u);
        });
      });
      const autoExpand = new Set();
      data.forEach(u => { if (cMap[u.id]) autoExpand.add(u.id); });
      setExpandedNodes(autoExpand);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const toggleNode = (id) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpandedNodes(new Set(users.map(u => u.id)));
  const collapseAll = () => setExpandedNodes(new Set());

  // Build parent -> children map
  const childrenMap = {};
  users.forEach(u => {
    (u.reporting_managers || []).forEach(mgrId => {
      if (!childrenMap[mgrId]) childrenMap[mgrId] = [];
      childrenMap[mgrId].push(u);
    });
  });

  // Root nodes: have reportees but no parent, exclude SA/CEO
  const hasParent = new Set();
  users.forEach(u => { if ((u.reporting_managers || []).length > 0) hasParent.add(u.id); });
  const rootNodes = users.filter(u =>
    !hasParent.has(u.id) && !['superadmin', 'ceo'].includes(u.role) && (childrenMap[u.id] || []).length > 0
  ).sort((a, b) => (ROLE_PRIORITY[a.role] || 99) - (ROLE_PRIORITY[b.role] || 99));

  // Unassigned: no parent, no children, not SA/CEO
  const unassigned = users.filter(u =>
    (!u.reporting_managers || u.reporting_managers.length === 0) &&
    !['superadmin', 'ceo'].includes(u.role) &&
    !(childrenMap[u.id] || []).length
  );

  // Stats
  const allManagerIds = new Set();
  users.forEach(u => (u.reporting_managers || []).forEach(mid => allManagerIds.add(mid)));
  const managersWithReports = users.filter(u => allManagerIds.has(u.id));
  const assignedCount = users.filter(u => (u.reporting_managers || []).length > 0).length;

  // Chain depth
  const getDepth = (uid, visited) => {
    if (visited.has(uid)) return 0;
    visited.add(uid);
    const kids = childrenMap[uid] || [];
    if (!kids.length) return 0;
    let max = 0;
    for (const k of kids) { const d = getDepth(k.id, visited); if (d > max) max = d; }
    return 1 + max;
  };
  const maxDepth = rootNodes.length > 0 ? Math.max(...rootNodes.map(r => getDepth(r.id, new Set()))) : 0;

  // Flatten tree for rendering
  const searchLower = search.toLowerCase();
  const flatList = flattenTree(rootNodes, childrenMap, expandedNodes, searchLower || '');

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <SpinnerGap size={32} className="animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reporting-structure-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">Reporting Structure</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Hierarchy: Recruiter → Lead → Asst Mgr → Manager → Onsite Mgr → CEO
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase text-slate-500 font-semibold">Total Users</p>
            <p className="text-2xl font-bold text-slate-800">{users.length}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase text-slate-500 font-semibold">With Reports</p>
            <p className="text-2xl font-bold text-blue-700">{managersWithReports.length}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase text-slate-500 font-semibold">Assigned</p>
            <p className="text-2xl font-bold text-emerald-700">{assignedCount}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase text-slate-500 font-semibold">Unassigned</p>
            <p className="text-2xl font-bold text-amber-700">{unassigned.length}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase text-slate-500 font-semibold">Chain Depth</p>
            <p className="text-2xl font-bold text-violet-700">{maxDepth + 1} levels</p>
          </CardContent>
        </Card>
      </div>

      {/* Search + Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-9"
            data-testid="reporting-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={expandAll} className="text-xs h-8" data-testid="expand-all-btn">
          Expand All
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAll} className="text-xs h-8" data-testid="collapse-all-btn">
          Collapse All
        </Button>
      </div>

      {/* Tree View */}
      {rootNodes.length === 0 && unassigned.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <TreeStructure size={40} className="mx-auto mb-3" />
          <p>No reporting structure configured yet.</p>
          {(isSuperadmin || isCeo) && (
            <p className="text-sm mt-1">Assign reporting managers in the Employee Directory.</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {flatList.length > 0 && (
            <Card className="border-slate-200 overflow-hidden" data-testid="hierarchy-tree-card">
              <CardHeader className="pb-2 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                  <TreeStructure size={18} className="text-blue-600" />
                  Organization Hierarchy
                  <Badge variant="outline" className="text-[10px] ml-auto">
                    {rootNodes.length} top-level
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {flatList.map(({ user, depth, hasChildren, isExpanded, childCount }) => {
                  const borderColor = DEPTH_BORDER[depth % DEPTH_BORDER.length];
                  return (
                    <div
                      key={user.id}
                      data-testid={'tree-node-' + user.id}
                      className={'flex items-center gap-3 py-2 px-3 hover:bg-slate-50/80 transition-colors' + (hasChildren ? ' cursor-pointer' : '') + (depth > 0 ? ' border-l-2 ' + borderColor : '')}
                      style={{ paddingLeft: 12 + depth * 24 }}
                      onClick={() => hasChildren && toggleNode(user.id)}
                    >
                      {hasChildren ? (
                        <button className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-600 shrink-0" data-testid={'toggle-' + user.id}>
                          {isExpanded ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                        </button>
                      ) : (
                        <div className="w-5 h-5 shrink-0" />
                      )}
                      <div className={'w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ' + (hasChildren ? 'bg-slate-200 text-slate-700' : 'bg-blue-50 text-blue-600')}>
                        {user.name ? user.name.charAt(0) : '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-800">{user.name}</span>
                        <span className="text-xs text-slate-400 ml-2 hidden sm:inline">{user.email}</span>
                      </div>
                      <Badge className={(ROLE_COLORS[user.role] || 'bg-slate-100 text-slate-600') + ' text-[9px] shrink-0'}>
                        {ROLE_LABELS[user.role] || user.role}
                      </Badge>
                      {user.work_location && (
                        <span className="text-[10px] text-slate-400 shrink-0 hidden lg:inline bg-slate-50 px-1.5 py-0.5 rounded">{user.work_location}</span>
                      )}
                      {user.team && (
                        <span className="text-xs text-slate-400 shrink-0 hidden md:inline">{user.team}</span>
                      )}
                      {hasChildren && (
                        <Badge variant="outline" className="text-[9px] shrink-0">{childCount}</Badge>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Unassigned Users */}
          {unassigned.length > 0 && (
            <Card className="border-amber-200" data-testid="unassigned-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
                  <Users size={18} className="text-amber-600" />
                  Unassigned Users
                  <Badge variant="outline" className="text-[10px]">{unassigned.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="divide-y divide-slate-50">
                  {unassigned
                    .filter(u => !search || u.name?.toLowerCase().includes(searchLower))
                    .map(u => (
                      <div key={u.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-amber-50/30" data-testid={'unassigned-' + u.id}>
                        <div className="w-5 h-5 shrink-0" />
                        <div className="w-7 h-7 rounded-full bg-amber-50 flex items-center justify-center text-[10px] font-bold text-amber-600">
                          {u.name ? u.name.charAt(0) : '?'}
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-medium text-slate-800">{u.name}</span>
                          <span className="text-xs text-slate-400 ml-2">{u.email}</span>
                        </div>
                        <Badge className={(ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-600') + ' text-[9px]'}>
                          {ROLE_LABELS[u.role] || u.role}
                        </Badge>
                        <span className="text-xs text-slate-400">{u.team || '-'}</span>
                      </div>
                    ))
                  }
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ReportingStructurePage;
